import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, ChevronLeft, ChevronRight, Star, MapPin, Plane, Building2, Users, Loader2, Globe, Utensils, Wine, Waves, Calendar, DollarSign, Percent, FileText, Clock, Flower2, Palmtree, BadgeCheck, Home, Flag } from 'lucide-react';
import { cn, convertDistance, getDistanceUnitLabel } from '@/lib/utils';
import { proposalTokens } from './tokens';
import { useVenuePhotos } from '@/hooks/useVenuePhotos';
import { formatEventDates, formatMonth, parseDateOnly } from '@/lib/dateFormatters';
import { formatCurrency } from '@/lib/currencyFormat';
import type { VenueDetails } from '@/services/ResearchService';
import type { VenueShortlistItem } from '@/services/ResearchService';
import { StarRating } from '@/components/ui/StarRating';

interface HotelDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  venue: VenueDetails | null;
  shortlistItem?: VenueShortlistItem | null;
  destinationName?: string;
  destinationCountry?: string;
  destinationId?: string;
  shareLinkToken?: string;
  sessionId?: string;
  onAddToFavorites?: (destinationId: string, hotelId: string) => void;
  isInFavorites?: boolean;
  eventDateType?: 'date_range' | 'month' | null;
  eventMonth?: string | null;
  eventStartDate?: string | null;
  eventEndDate?: string | null;
  eventDateDisplayOverride?: string | null;
  distanceUnit?: 'kilometers' | 'miles' | null;
}

export function HotelDetailsModal({
  isOpen,
  onClose,
  venue,
  shortlistItem,
  destinationName = '',
  destinationCountry = '',
  destinationId,
  shareLinkToken,
  sessionId,
  onAddToFavorites,
  isInFavorites = false,
  eventDateType,
  eventMonth,
  eventStartDate,
  eventEndDate,
  eventDateDisplayOverride,
  distanceUnit = 'kilometers'
}: HotelDetailsModalProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('pricing');
  
  // Fetch venue photos (custom if exist, else Google)
  const { photos, isLoading: isLoadingPhotos } = useVenuePhotos(
    venue?.id || null,
    isOpen,
    shareLinkToken,
    sessionId
  );

  if (!isOpen || !venue) return null;

  // Use Google photos if available, otherwise fallback to placeholder
  const hotelImages = photos.length > 0 
    ? photos.map(p => p.url)
    : ['https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80'];

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  const goNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % hotelImages.length);
  };

  const goPrev = () => {
    setCurrentImageIndex((prev) => (prev - 1 + hotelImages.length) % hotelImages.length);
  };


  const formatPercent = (percent: number | null | undefined): string => {
    if (percent === null || percent === undefined) return '-';
    return `${percent.toFixed(2)}%`;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = parseDateOnly(dateString) || new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Get price range - only show lowest room rate (per night)
  // Check override first, then fall back to original value
  const displayLowestRate = shortlistItem?.override_lowest_room_rate ?? shortlistItem?.cvent_lowest_room_rate;
  
  const priceRange = displayLowestRate
    ? `${formatCurrency(displayLowestRate, shortlistItem?.cvent_currency_code || 'USD')}/night`
    : 'Contact for pricing';

  // Manually-added RFPs: rate + dates entered by hand (no Cvent proposal data)
  const isManualProposal = shortlistItem?.is_manual_proposal === true;
  const manualCheckIn = shortlistItem?.proposal_check_in_date || null;
  const manualCheckOut = shortlistItem?.proposal_check_out_date || null;

  // Get airport info
  const airportCode = venue.nearest_airport_iata || 'N/A';
  const distLabel = getDistanceUnitLabel(distanceUnit);
  const airportDistance = venue.nearest_airport_distance_km
    ? `${(convertDistance(Number(venue.nearest_airport_distance_km), distanceUnit) ?? 0).toFixed(1)} ${distLabel}`
    : 'N/A';

  // Approx. ground travel time from the airport, estimated from distance (no routing data
  // available). Assumes ~50 km/h average transfer speed (accounts for airport access + traffic).
  const AVG_TRANSFER_KMH = 50;
  const airportTravelTime = venue.nearest_airport_distance_km
    ? (() => {
        const km = Number(venue.nearest_airport_distance_km);
        const totalMin = Math.max(5, Math.round((km / AVG_TRANSFER_KMH) * 60 / 5) * 5); // round to 5 min, floor 5
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        if (h > 0) return `${h} hr${h > 1 ? 's' : ''}${m ? ` ${m} min` : ''}`;
        return `${m} min`;
      })()
    : null;

  // Extract Cvent proposal data - match ProposalBreakdownModal structure exactly
  // In ProposalBreakdownModal, proposalData is the full JSONB object (with _summary added)
  // Here we need to extract it from shortlistItem and add _summary
  const proposalDataRaw = shortlistItem?.cvent_proposal_data_jsonb;
  const proposalData: any = proposalDataRaw ? {
    ...proposalDataRaw,
    _summary: shortlistItem?.cvent_proposal_summary || null,
  } : null;
  
  // Extract summary if passed directly or through proposalData (matching ProposalBreakdownModal)
  const proposalSummary = shortlistItem?.cvent_proposal_summary || proposalData?._summary || null;
  
  // Extract data from proposal - match ProposalBreakdownModal exactly
  // Handle both cases: proposalData might be the full object with .proposal, or proposalData might BE the proposal
  const proposal = proposalData?.proposal || proposalData || {};
  const eventAvailability = proposal.eventAvailability || {};
  const sleepingRoomAvailability = proposal.sleepingRoomAvailability || {};
  const meetingSpaceAvailability = proposal.meetingSpaceAvailability || {};
  const currencyCode = shortlistItem?.cvent_currency_code || 'USD';

  // Dates - match ProposalBreakdownModal exactly (no fallback to props)
  const startDate = eventAvailability.startDate || proposal.agendaStartDate;
  const endDate = eventAvailability.endDate;
  const availability = eventAvailability.availability || '';

  // Room rates and details - match ProposalBreakdownModal exactly
  const sleepingRooms = proposal.sleepingRooms || [];
  const lowestRoomRate = proposal.proposalDerivedDetail?.lowestRoomRate;
  const maxRoomRate = proposal.proposalDerivedDetail?.maxRoomRate;
  const roomAvailabilityInfo = sleepingRoomAvailability.additionalInfo || '';
  const sleepingRoomReqMet = sleepingRoomAvailability.sleepingRoomReqMetType || '';

  // Taxes and fees - match ProposalBreakdownModal exactly
  const applicableTaxPercent = proposal.applicableTaxPercent ?? null;
  const serviceChargePercent = proposal.serviceChargePercent ?? null;
  const isTaxInclusive = proposal.isTaxInclusive ?? false;
  const taxAdditionalInfo = proposal.taxAdditionalInfo || '';

  // F&B - match ProposalBreakdownModal exactly
  const foodBeverageMinimum = proposal.foodBeverageMinimum ?? null;
  const foodBeverageMinimumNote = proposal.foodBeverageMinimumNote || '';
  const isFnbCost = proposal.isFnbCost ?? false;
  const estimatedCostsAdditionalInfo = proposal.estimatedCostsAdditionalInfo || '';

  // Meeting room costs - match ProposalBreakdownModal exactly
  const meetingRoomInfo = meetingSpaceAvailability.additionalInfo || '';
  const avNeeds = meetingSpaceAvailability.avNeeds || '';

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className={cn(
          "w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] animate-fade-in relative",
          "bg-cream"
        )}
        style={{ backgroundColor: proposalTokens.colors.cream }}
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-md bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5" style={{ color: proposalTokens.colors.teal }} />
        </button>

        {/* Content Container - Scrollable */}
        <div className="overflow-y-auto max-h-[90vh]">
          {/* Image Gallery */}
          <div className="relative h-[300px] md:h-[400px] w-full bg-black overflow-hidden">
            {isLoadingPhotos ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-white" />
              </div>
            ) : (
              <>
                {hotelImages.map((image: string, index: number) => (
                  <div
                    key={index}
                    className={cn(
                      "absolute inset-0 transition-opacity duration-500",
                      index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    )}
                  >
                    <img 
                      src={image} 
                      className="w-full h-full object-cover" 
                      alt={`${venue.property_name} - Image ${index + 1}`}
                    />
                  </div>
                ))}
              </>
            )}

            {/* Navigation Arrows */}
            {hotelImages.length > 1 && !isLoadingPhotos && (
              <>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    goPrev();
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-md bg-white/20 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/40 transition-all z-10"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    goNext();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-md bg-white/20 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/40 transition-all z-10"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Image Indicators */}
            {hotelImages.length > 1 && !isLoadingPhotos && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
                {hotelImages.map((_, index: number) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToImage(index);
                    }}
                    className={cn(
                      "h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex 
                        ? 'w-8 bg-white' 
                        : 'w-2 bg-white/50 hover:bg-white/80'
                    )}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 md:p-10" style={{ backgroundColor: proposalTokens.colors.cream }}>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h2 className="text-2xl md:text-3xl font-serif font-bold mb-3" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
                    {venue.property_name}
                  </h2>
                  {venue.address && (
                    <div className="flex items-center gap-2 text-sm mb-2" style={{ color: `${proposalTokens.colors.teal}99` }}>
                      <MapPin className="w-4 h-4" />
                      <span>{venue.address}</span>
                    </div>
                  )}
                  {venue.website && (
                    <div className="flex items-center gap-2 text-sm" style={{ color: `${proposalTokens.colors.teal}99` }}>
                      <Globe className="w-4 h-4" />
                      <a
                        href={venue.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                        style={{ color: proposalTokens.colors.teal }}
                      >
                        Visit Hotel Website
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {/* Stars */}
                  {venue.star_rating && (
                    <div className="flex items-center gap-1">
                      <StarRating rating={venue.star_rating} size="md" showEmptyStars={true} goldColor={proposalTokens.colors.gold} />
                    </div>
                  )}
                  {/* Price */}
                  <div className="text-right">
                    <p className="text-xs uppercase font-bold mb-1" style={{ color: `${proposalTokens.colors.teal}66` }}>From</p>
                    <p className="text-xl font-bold" style={{ color: proposalTokens.colors.teal }}>{priceRange}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 mt-6">
              {/* Rooms */}
              {venue.number_of_rooms && (
                <div className="p-4 bg-white rounded-2xl border border-black/5">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${proposalTokens.colors.gold}1a` }}>
                    <Building2 className="w-5 h-5" style={{ color: proposalTokens.colors.gold }} />
                  </div>
                  <p className="text-xs uppercase font-bold mb-1" style={{ color: `${proposalTokens.colors.teal}66` }}>Rooms</p>
                  <p className="text-lg font-bold" style={{ color: proposalTokens.colors.teal }}>{venue.number_of_rooms}</p>
                </div>
              )}

              {/* Meeting Spaces */}
              {venue.meeting_spaces && (
                <div className="p-4 bg-white rounded-2xl border border-black/5">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${proposalTokens.colors.teal}1a` }}>
                    <Users className="w-5 h-5" style={{ color: proposalTokens.colors.teal }} />
                  </div>
                  <p className="text-xs uppercase font-bold mb-1" style={{ color: `${proposalTokens.colors.teal}66` }}>Meeting Spaces</p>
                  <p className="text-lg font-bold" style={{ color: proposalTokens.colors.teal }}>{venue.meeting_spaces}</p>
                  {venue.maximum_capacity && (
                    <p className="text-xs mt-1" style={{ color: `${proposalTokens.colors.teal}99` }}>Max: {venue.maximum_capacity}</p>
                  )}
                </div>
              )}

              {/* Airport Code */}
              <div className="p-4 bg-white rounded-2xl border border-black/5">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${proposalTokens.colors.teal}1a` }}>
                  <Plane className="w-5 h-5" style={{ color: proposalTokens.colors.teal }} />
                </div>
                <p className="text-xs uppercase font-bold mb-1" style={{ color: `${proposalTokens.colors.teal}66` }}>Airport</p>
                <p className="text-lg font-bold" style={{ color: proposalTokens.colors.teal }}>{airportCode}</p>
              </div>

              {/* Airport Distance */}
              <div className="p-4 bg-white rounded-2xl border border-black/5">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${proposalTokens.colors.teal}1a` }}>
                  <MapPin className="w-5 h-5" style={{ color: proposalTokens.colors.teal }} />
                </div>
                <p className="text-xs uppercase font-bold mb-1" style={{ color: `${proposalTokens.colors.teal}66` }}>Distance to Airport</p>
                <p className="text-lg font-bold" style={{ color: proposalTokens.colors.teal }}>{airportDistance}</p>
                {airportTravelTime && (
                  <p className="text-xs font-medium mt-1 flex items-center gap-1" style={{ color: `${proposalTokens.colors.teal}99` }}>
                    <Clock className="w-3 h-3" />
                    Approx. Travel Time: {airportTravelTime}
                  </p>
                )}
              </div>
            </div>

            {/* Client-facing hotel note (the public API never returns internal notes) */}
            {shortlistItem?.notes?.trim() && (
              <div className="p-5 bg-white rounded-2xl border border-black/5 mb-6" data-testid="client-note">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4" style={{ color: proposalTokens.colors.teal }} />
                  <h4 className="text-sm uppercase font-bold tracking-wide" style={{ color: proposalTokens.colors.teal }}>
                    Notes
                  </h4>
                </div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: proposalTokens.colors.teal }}>
                  {shortlistItem.notes}
                </p>
              </div>
            )}

            {/* Tabs */}
            <div>
              <div className="flex gap-2 mb-6 border-b border-black/10">
                <button
                  onClick={() => setActiveTab('pricing')}
                  className={cn(
                    "px-4 py-2 text-sm font-semibold transition-colors border-b-2",
                    activeTab === 'pricing'
                      ? 'border-teal text-teal'
                      : 'border-transparent text-black/40 hover:text-black/60'
                  )}
                  style={activeTab === 'pricing' ? { borderColor: proposalTokens.colors.teal, color: proposalTokens.colors.teal } : {}}
                >
                  Pricing
                </button>
                <button
                  onClick={() => setActiveTab('amenities')}
                  className={cn(
                    "px-4 py-2 text-sm font-semibold transition-colors border-b-2",
                    activeTab === 'amenities'
                      ? 'border-teal text-teal'
                      : 'border-transparent text-black/40 hover:text-black/60'
                  )}
                  style={activeTab === 'amenities' ? { borderColor: proposalTokens.colors.teal, color: proposalTokens.colors.teal } : {}}
                >
                  Amenities
                </button>
              </div>

              {/* Amenities Tab */}
              {activeTab === 'amenities' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    {venue.number_of_restaurants !== null && venue.number_of_restaurants !== undefined && (
                      <div className="flex items-center gap-2">
                        <Utensils className="w-4 h-4 shrink-0" style={{ color: proposalTokens.colors.teal }} />
                        <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>Restaurants:</span>
                        <span style={{ color: `${proposalTokens.colors.teal}99` }}>{venue.number_of_restaurants}</span>
                      </div>
                    )}

                    {venue.number_of_bars !== null && venue.number_of_bars !== undefined && (
                      <div className="flex items-center gap-2">
                        <Wine className="w-4 h-4 shrink-0" style={{ color: proposalTokens.colors.teal }} />
                        <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>Bars:</span>
                        <span style={{ color: `${proposalTokens.colors.teal}99` }}>{venue.number_of_bars}</span>
                      </div>
                    )}

                    {venue.number_of_pools !== null && venue.number_of_pools !== undefined && (
                      <div className="flex items-center gap-2">
                        <Waves className="w-4 h-4 shrink-0" style={{ color: proposalTokens.colors.teal }} />
                        <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>Pools:</span>
                        <span style={{ color: `${proposalTokens.colors.teal}99` }}>{venue.number_of_pools}</span>
                      </div>
                    )}

                    {venue.spa_facilities !== null && (
                      <div className="flex items-center gap-2">
                        <Flower2 className="w-4 h-4 shrink-0" style={{ color: proposalTokens.colors.teal }} />
                        <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>Spa Facilities:</span>
                        <span style={{ color: `${proposalTokens.colors.teal}99` }}>{venue.spa_facilities ? 'Yes' : 'No'}</span>
                      </div>
                    )}

                    {/* Only real beach venues get the beach row. beach_access is an enum:
                        'beachfront' | 'beach_access' | 'none' | null — and the string 'none'
                        is truthy, so a plain truthiness check used to show "Beach Access: Yes"
                        for hotels with no beach at all. */}
                    {(venue.beach_access === 'beachfront' || venue.beach_access === 'beach_access') && (
                      <div className="flex items-center gap-2">
                        <Palmtree className="w-4 h-4 shrink-0" style={{ color: proposalTokens.colors.teal }} />
                        <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>
                          {venue.beach_access === 'beachfront' ? 'Beachfront:' : 'Beach Access:'}
                        </span>
                        <span style={{ color: `${proposalTokens.colors.teal}99` }}>Yes</span>
                      </div>
                    )}

                    {venue.all_inclusive_available !== null && (
                      <div className="flex items-center gap-2">
                        <BadgeCheck className="w-4 h-4 shrink-0" style={{ color: proposalTokens.colors.teal }} />
                        <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>All-Inclusive:</span>
                        <span style={{ color: `${proposalTokens.colors.teal}99` }}>{venue.all_inclusive_available ? 'Available' : 'Not Available'}</span>
                      </div>
                    )}

                    {venue.property_type && (
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4 shrink-0" style={{ color: proposalTokens.colors.teal }} />
                        <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>Property Type:</span>
                        <span style={{ color: `${proposalTokens.colors.teal}99` }}>{venue.property_type}</span>
                      </div>
                    )}

                    {venue.golf_course !== null && (
                      <div className="flex items-center gap-2">
                        <Flag className="w-4 h-4 shrink-0" style={{ color: proposalTokens.colors.teal }} />
                        <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>Golf Course:</span>
                        <span style={{ color: `${proposalTokens.colors.teal}99` }}>
                          {venue.golf_course === 'onsite' ? 'On-site' :
                           venue.golf_course === 'nearby' ? 'Nearby (within 5km)' :
                           venue.golf_course === 'none' ? 'None' : 'N/A'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === 'pricing' && (
                <div className="space-y-6">
                  {/* Event Dates block removed per client request (Pricing cleanup) */}

                  {/* Summary (if available) */}
                  {proposalSummary && (
                    <div className="p-4 bg-white rounded-2xl border border-black/5">
                      <div className="flex items-center gap-2 mb-3">
                        <DollarSign className="w-5 h-5" style={{ color: proposalTokens.colors.teal }} />
                        <h3 className="text-lg font-semibold" style={{ color: proposalTokens.colors.teal }}>Summary</h3>
                      </div>
                      <p className="text-xs mb-3" style={{ color: `${proposalTokens.colors.teal}99` }}>
                        Breakdown of proposal costs and details
                      </p>
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown
                          components={{
                            h1: ({...props}: any) => <h1 className="text-xl font-semibold mb-3 mt-4 first:mt-0" style={{ color: proposalTokens.colors.teal }} {...props} />,
                            h2: ({...props}: any) => <h2 className="text-lg font-semibold mb-2 mt-3 first:mt-0" style={{ color: proposalTokens.colors.teal }} {...props} />,
                            h3: ({...props}: any) => <h3 className="text-base font-semibold mb-2 mt-2 first:mt-0" style={{ color: proposalTokens.colors.teal }} {...props} />,
                            p: ({...props}: any) => <p className="mb-2 text-sm leading-relaxed" style={{ color: 'rgba(0,0,0,0.8)' }} {...props} />,
                            ul: ({...props}: any) => <ul className="list-disc list-inside mb-2 space-y-1 ml-2" style={{ color: 'rgba(0,0,0,0.8)' }} {...props} />,
                            ol: ({...props}: any) => <ol className="list-decimal list-inside mb-2 space-y-1 ml-2" style={{ color: 'rgba(0,0,0,0.8)' }} {...props} />,
                            li: ({...props}: any) => <li className="text-sm" style={{ color: 'rgba(0,0,0,0.8)' }} {...props} />,
                            strong: ({...props}: any) => <strong className="font-semibold" style={{ color: proposalTokens.colors.teal }} {...props} />,
                            em: ({...props}: any) => <em className="italic" {...props} />,
                          }}
                        >
                          {proposalSummary?.replace(/\n(?!\n)/g, '  \n')}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* Room Rates */}
                  <div className="p-4 bg-white rounded-2xl border border-black/5">
                    <div className="flex items-center gap-2 mb-3">
                      <Building2 className="w-5 h-5" style={{ color: proposalTokens.colors.teal }} />
                      <h3 className="text-lg font-semibold" style={{ color: proposalTokens.colors.teal }}>Room Rates</h3>
                    </div>
                    <div className="space-y-4">
                      {/* Dates and Rate Summary */}
                      {startDate && (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-medium" style={{ color: `${proposalTokens.colors.teal}99` }}>Dates:</span>
                            <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>
                              {formatDate(startDate)}
                              {endDate && ` - ${formatDate(endDate)}`}
                            </span>
                          </div>
                          {(lowestRoomRate !== null && lowestRoomRate !== undefined) && (
                            <div className="flex justify-between items-center">
                              <span style={{ color: `${proposalTokens.colors.teal}99` }}>Rate:</span>
                              <div className="flex items-center gap-2">
                                {lowestRoomRate && (
                                  <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>
                                    {formatCurrency(lowestRoomRate, currencyCode)}
                                  </span>
                                )}
                                {lowestRoomRate && maxRoomRate && lowestRoomRate !== maxRoomRate && (
                                  <>
                                    <span style={{ color: `${proposalTokens.colors.teal}99` }}>-</span>
                                    <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>
                                      {formatCurrency(maxRoomRate, currencyCode)}
                                    </span>
                                  </>
                                )}
                                <span className="text-xs" style={{ color: `${proposalTokens.colors.teal}99` }}>per room, per night</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Room Categories & Room Availability Details removed per client request (Pricing cleanup) */}

                      {/* Manually-added RFP: show hand-entered rate + dates when no Cvent proposal data */}
                      {!startDate && isManualProposal && (displayLowestRate != null || manualCheckIn) && (
                        <div className="space-y-2">
                          {(manualCheckIn || manualCheckOut) && (
                            <div className="flex justify-between items-center">
                              <span className="font-medium" style={{ color: `${proposalTokens.colors.teal}99` }}>Dates:</span>
                              <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>
                                {manualCheckIn ? formatDate(manualCheckIn) : 'N/A'}
                                {manualCheckOut && ` - ${formatDate(manualCheckOut)}`}
                              </span>
                            </div>
                          )}
                          {displayLowestRate != null && (
                            <div className="flex justify-between items-center">
                              <span style={{ color: `${proposalTokens.colors.teal}99` }}>Rate:</span>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold" style={{ color: proposalTokens.colors.teal }}>
                                  {formatCurrency(displayLowestRate, currencyCode)}
                                </span>
                                <span className="text-xs" style={{ color: `${proposalTokens.colors.teal}99` }}>per room, per night</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Taxes and Fees */}
                      {(applicableTaxPercent !== null || serviceChargePercent !== null || taxAdditionalInfo) && (
                        <div className="pt-2 border-t border-black/10">
                          <h4 className="font-medium mb-2 text-sm" style={{ color: proposalTokens.colors.teal }}>Taxes & Fees:</h4>
                          <div className="space-y-2 text-sm">
                            {applicableTaxPercent !== null && (
                              <div className="flex justify-between">
                                <span style={{ color: `${proposalTokens.colors.teal}99` }}>Tax:</span>
                                <span className="font-medium" style={{ color: proposalTokens.colors.teal }}>
                                  {formatPercent(applicableTaxPercent)}
                                  {isTaxInclusive && <span className="text-xs ml-1" style={{ color: `${proposalTokens.colors.teal}99` }}>(inclusive)</span>}
                                </span>
                              </div>
                            )}
                            {serviceChargePercent !== null && (
                              <div className="flex justify-between">
                                <span style={{ color: `${proposalTokens.colors.teal}99` }}>Service Charge:</span>
                                <span className="font-medium" style={{ color: proposalTokens.colors.teal }}>{formatPercent(serviceChargePercent)}</span>
                              </div>
                            )}
                            {/* Taxes additional info removed per client request (Pricing cleanup) */}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Food & Beverage */}
                  {(foodBeverageMinimum !== null || isFnbCost || estimatedCostsAdditionalInfo || applicableTaxPercent !== null || serviceChargePercent !== null) && (
                    <div className="p-4 bg-white rounded-2xl border border-black/5">
                      <div className="flex items-center gap-2 mb-3">
                        <Utensils className="w-5 h-5" style={{ color: proposalTokens.colors.teal }} />
                        <h3 className="text-lg font-semibold" style={{ color: proposalTokens.colors.teal }}>Food & Beverage</h3>
                      </div>
                      <div className="space-y-3">
                        {foodBeverageMinimum !== null && (
                          <div className="pb-2 border-b border-black/10">
                            <div className="flex justify-between items-center">
                              <span className="font-medium" style={{ color: `${proposalTokens.colors.teal}99` }}>F&B Minimum:</span>
                              <span className="font-semibold text-lg" style={{ color: proposalTokens.colors.teal }}>
                                {formatCurrency(foodBeverageMinimum, currencyCode)}
                              </span>
                            </div>
                            {foodBeverageMinimumNote && (
                              <p className="text-xs mt-1" style={{ color: `${proposalTokens.colors.teal}99` }}>{foodBeverageMinimumNote}</p>
                            )}
                          </div>
                        )}

                        {/* F&B Taxes and Service Charges */}
                        {(applicableTaxPercent !== null || serviceChargePercent !== null) && (
                          <div>
                            <h4 className="font-medium mb-2 text-sm" style={{ color: proposalTokens.colors.teal }}>F&B Charges:</h4>
                            <div className="space-y-2 text-sm">
                              {serviceChargePercent !== null && (
                                <div className="flex justify-between">
                                  <span style={{ color: `${proposalTokens.colors.teal}99` }}>Service Fee:</span>
                                  <span className="font-medium" style={{ color: proposalTokens.colors.teal }}>{formatPercent(serviceChargePercent)}</span>
                                </div>
                              )}
                              {applicableTaxPercent !== null && (
                                <div className="flex justify-between">
                                  <span style={{ color: `${proposalTokens.colors.teal}99` }}>F&B Tax:</span>
                                  <span className="font-medium" style={{ color: proposalTokens.colors.teal }}>{formatPercent(applicableTaxPercent)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Additional F&B Information removed per client request (Pricing cleanup) */}
                      </div>
                    </div>
                  )}

                  {/* Meeting Room Costs block removed per client request (Pricing cleanup) */}

                </div>
              )}
            </div>

            {/* Add to Favorites Button */}
            {onAddToFavorites && destinationId && venue && (
              <div className="mt-8 pt-6 border-t border-black/10">
                <button
                  onClick={() => {
                    onAddToFavorites(destinationId, venue.id);
                  }}
                  disabled={isInFavorites}
                  className={`
                    w-full font-bold py-3 px-6 rounded-md transition-all shadow-md
                    ${isInFavorites
                      ? 'bg-teal/20 text-teal/60 cursor-not-allowed'
                      : 'hover:scale-105'
                    }
                  `}
                  style={isInFavorites 
                    ? {} 
                    : { 
                        backgroundColor: proposalTokens.colors.gold, 
                        color: '#000000',
                        boxShadow: `0 10px 15px -3px ${proposalTokens.colors.gold}33`
                      }
                  }
                >
                  {isInFavorites ? 'Added to Shortlist' : 'Add to Shortlist'}
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
